import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sites, categories } from '@/mocks/sites';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const category = categories.find((c) => c.slug === slug);
  const categorySites = sites.filter((s) => s.category === slug);

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-100/70 text-primary-700',
    accent: 'bg-accent-100/80 text-accent-700',
    secondary: 'bg-secondary-100/80 text-secondary-700',
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-600">{t('noResults')}</p>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const iconColor = colorMap[category.color] || colorMap.primary;

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: t('categories'), to: '/category/zonghe-menhu' },
              { label: category.name },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${iconColor}`}>
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600">{category.description}</p>
              </div>
            </div>
            <p className="text-xs text-foreground-500 ml-15">
              共收录 <strong>{categorySites.length}</strong> {t('websiteCount')}
            </p>
          </div>

          <div className="flex gap-6">
            <aside className="hidden lg:block w-48 flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
                  {t('allCategories')}
                </h3>
                <nav className="space-y-0.5">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className={`block px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                        c.slug === slug
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 flex items-center justify-center`}>
                          <i className={`${c.icon} text-xs`}></i>
                        </span>
                        {c.name}
                      </span>
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0 pb-12">
              {categorySites.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center">
                    <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">{t('noResults')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
                  {categorySites.map((site) => (
                    <a
                      key={site.id}
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group p-4 md:p-5 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 text-primary-700 font-bold text-sm">
                          {site.name.charAt(0)}
                        </div>
                        <div className="w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-external-link-line text-xs text-foreground-400"></i>
                        </div>
                      </div>
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1.5 whitespace-nowrap">
                        {site.name}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {site.description}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {site.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded text-xs bg-background-200/70 text-foreground-500"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}